import { Clock, Gift, Sparkles } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";
import { toast } from "sonner";
import { useSubscription } from "~/lib/hooks/use-subscription";

export function TrialBanner() {
	const { isTrialing, trialDaysRemaining, trialAlreadyExtended, extendTrial } = useSubscription();
	const [extending, setExtending] = useState(false);
	const [extendError, setExtendError] = useState<string | null>(null);

	if (!isTrialing || trialDaysRemaining <= 0) {
		return null;
	}

	const daysText =
		trialDaysRemaining === 1
			? "1 den"
			: trialDaysRemaining < 5
				? `${trialDaysRemaining} dny`
				: `${trialDaysRemaining} dní`;

	// Days 1-3: warning; 4-7: mid; 8+: welcome
	const isUrgent = trialDaysRemaining <= 3;
	const isMid = !isUrgent && trialDaysRemaining <= 7;
	const canExtend = isUrgent && !trialAlreadyExtended;

	async function handleExtend() {
		setExtendError(null);
		setExtending(true);
		const result = await extendTrial();
		setExtending(false);
		if (!result.ok) {
			setExtendError("Prodloužení se nezdařilo. Zkuste to prosím znovu.");
		} else {
			toast.success("Zkušební období bylo prodlouženo o 14 dní.");
		}
	}

	if (isUrgent) {
		return (
			<div className="flex flex-wrap items-center justify-between gap-3 border-b border-warning/20 bg-warning/10 px-8 py-2.5">
				<div className="flex items-center gap-2 text-sm text-warning-content">
					<Clock className="h-4 w-4" />
					<span>
						Vaše zkušební verze Premium vyprší za <strong>{daysText}</strong>.
					</span>
					{extendError && <span className="text-xs text-error">{extendError}</span>}
				</div>
				<div className="flex items-center gap-3">
					{canExtend && (
						<button
							type="button"
							onClick={handleExtend}
							disabled={extending}
							className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline disabled:opacity-60"
						>
							<Gift className="h-4 w-4" />
							{extending ? "Prodlužuji…" : "Prodloužit o 14 dní zdarma"}
						</button>
					)}
					<Link to="/cenik" className="text-sm font-semibold text-primary hover:underline">
						Upgradovat
					</Link>
				</div>
			</div>
		);
	}

	if (isMid) {
		return (
			<div className="flex items-center justify-between border-b border-primary/20 bg-primary/5 px-8 py-2.5">
				<div className="flex items-center gap-2 text-sm text-foreground">
					<Clock className="h-4 w-4 text-primary" />
					<span>
						Premium zdarma ještě <strong>{daysText}</strong>. Po skončení automaticky přejdete na
						plán Zdarma.
					</span>
				</div>
				<Link to="/cenik" className="text-sm font-semibold text-primary hover:underline">
					Upgradovat
				</Link>
			</div>
		);
	}

	// Days 8-14 — welcome state
	return (
		<div className="flex items-center justify-between border-b border-primary/20 bg-primary/5 px-8 py-2.5">
			<div className="flex items-center gap-2 text-sm text-foreground">
				<Sparkles className="h-4 w-4 text-primary" />
				<span>
					Vyzkoušejte Premium zdarma — zbývá <strong>{daysText}</strong>.
				</span>
			</div>
			<Link to="/cenik" className="text-sm font-semibold text-primary hover:underline">
				Zobrazit plány
			</Link>
		</div>
	);
}
